import React, { createContext, FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { lazy } from 'yup';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';

import Disclaimers from '@buyside/components/presentation/WhitePaperForm/Disclaimers/disclaimers';
import TitleAndCategories from '@buyside/components/presentation/WhitePaperForm/TitleAndCategories/titleAndCategories';
import WriteupAndAttachments from '@buyside/components/presentation/WhitePaperForm/WriteupAndAttachments/writeupAndAttachments';
import ResearchFormWizard, {
  ResearchWizardStep
} from '@buyside/components/presentation/ResearchFormWizard/researchFormWizard';

import {
  determineWhitePaperCompletedSteps,
  generateWhitePaperValidationSchema,
  generateWhitepaperDefaultValues,
  WhitePaperFormInput
} from '@buyside/util/researchSubmission';

import {
  WhitePaperSubmissionQuery,
  useCreateOrUpdateWhitePaperMutation
} from '@interfaces/graphql-typings';

export const WhitePaperFormContext = createContext<{
  currentUser?: WhitePaperSubmissionQuery['currentUser'];
  whitePaperId?: string;
  isDraft: boolean;
}>({ isDraft: true });

const steps = [
  {
    label: 'Title & Categories',
    name: 'title_and_categories',
    component: TitleAndCategories
  },
  {
    label: 'Writeup & Attachments',
    name: 'writeup_and_attachments',
    component: WriteupAndAttachments
  },
  {
    label: 'Review & Submit',
    name: 'review_and_submit',
    component: Disclaimers
  }
];

const WhitePaperForm: FC<{
  data?: WhitePaperSubmissionQuery;
  id?: string;
}> = ({ data, id }) => {
  const navigate = useNavigate();

  const { currentUser, whitePaper } = data || {};
  const { id: currentUserId, isAdmin } = currentUser || {};
  const { status, user: whitePaperUser } = whitePaper || {};
  const { id: whitePaperUserId } = whitePaperUser || {};

  const values = useMemo(() => generateWhitepaperDefaultValues(whitePaper), [whitePaper]);

  const completedStepNames = useMemo(
    () => (values ? determineWhitePaperCompletedSteps(values) : []),
    [values]
  );

  const initialStep = useMemo(
    () => steps.find(step => !completedStepNames.includes(step.name)) || steps[steps.length - 1],
    [completedStepNames]
  );

  const [currentStep, setCurrentStep] = useState<ResearchWizardStep>(id ? initialStep : steps[0]);

  const methods = useForm<WhitePaperFormInput>({
    resolver: yupResolver(
      lazy(() =>
        generateWhitePaperValidationSchema({
          currentStepName: currentStep.name
        })
      )
    ),
    values
  });

  const handleSetCurrentStep = useCallback((step: ResearchWizardStep) => {
    window.scrollTo(0, 0);
    return setCurrentStep(step);
  }, []);

  const [handleCreateOrUpdateWhitePaper] = useCreateOrUpdateWhitePaperMutation();

  const completedSteps = useMemo(
    () => (completedStepNames ? steps.filter(step => completedStepNames.includes(step.name)) : []),
    [completedStepNames]
  );

  const handleCreateOrUpdateDraft = useCallback(
    (values: WhitePaperFormInput, nextStep: ResearchWizardStep, isSubmission: boolean) => {
      const variables = {
        ...values,
        attachments: values.attachments.map(
          ({ id, attachmentType, file, filename, researchType }) => ({
            id,
            attachmentType,
            file,
            filename,
            researchType
          })
        ),
        currentStep: currentStep.name,
        isSubmission,
        mentionedEntities: values.mentionedEntities.map(({ value }) => value)
      };

      return handleCreateOrUpdateWhitePaper({ variables })
        .then(response => {
          handleSetCurrentStep(nextStep);
          const whitePaper = response.data?.createOrUpdateWhitePaper?.whitePaper;
          if (!id && whitePaper?.id) navigate(whitePaper.id, { replace: true });
        })
        .catch(error => toast.error(error.message, { autoClose: 7500, theme: 'colored' }));
    },
    [currentStep.name, handleCreateOrUpdateWhitePaper, handleSetCurrentStep, navigate, id]
  );

  if (whitePaperUserId && whitePaperUserId !== currentUserId && !isAdmin)
    return (
      <Navigate
        to="/"
        state={{
          cannotEditError: `You cannot edit Macro/Thematic Report with ID ${id}. Contact support@sumzero.com if you believe this is an error.`
        }}
      />
    );

  const isDraft = status === 'draft' || !whitePaper;

  return (
    <WhitePaperFormContext.Provider
      value={{
        currentUser,
        whitePaperId: id,
        isDraft
      }}
    >
      <FormProvider {...methods}>
        <ResearchFormWizard
          completedSteps={completedSteps}
          currentStep={currentStep}
          handleCreateOrUpdateDraft={handleCreateOrUpdateDraft}
          handleSetCurrentStep={handleSetCurrentStep}
          isDraft={isDraft}
          researchPath={`/pro/research/white_papers/${id}`}
          researchType="Macro/Thematic Report"
          steps={steps}
        />
      </FormProvider>
    </WhitePaperFormContext.Provider>
  );
};

export default WhitePaperForm;
